import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { DrawerService } from '../../services/drawer.service';
import { UiHeadingDirective } from '../../ui/heading/heading.directive';
import { UiIconButtonComponent } from '../../ui/icon-button/icon-button.component';

export type DrawerLayoutState = 'ready' | 'loading' | 'error';
export type DrawerLayoutBody = 'padded' | 'flush';

/**
 * The page-template drawer sheet and mobile panel point `aria-labelledby` here. Only one
 * drawer body is mounted at a time, so one stable id names whichever drawer is open.
 */
export const DRAWER_TITLE_ID = 'app-drawer-title';

/**
 * Frame for every route-backed drawer: one header (optional leading visual, title,
 * subtitle, trailing slot, and the close button) and a body that owns scrolling. End the
 * body with `app-drawer-footer` for actions.
 *
 * Slots:
 * - `[drawerLeading]` avatar, logo, or progress ring before the title
 * - `[drawerSubtitle]` rich subtitle content after the `subtitle` text
 * - `[drawerTrailing]` header stat or action menu, placed before the close button
 * - default content: the body
 *
 * While `state` is `loading` or `error` the header and close button still render, so a
 * drawer can always be dismissed, and the body shows the matching message instead of
 * projected content.
 */
@Component({
  selector: 'app-drawer-layout',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiHeadingDirective, UiIconButtonComponent],
  host: { class: 'flex h-full min-h-0 w-full flex-1 flex-col bg-surface' },
  template: `
    <header class="flex shrink-0 items-center gap-4 border-b border-border bg-surface-elevated py-4 pl-6 pr-4">
      <div class="shrink-0 empty:hidden"><ng-content select="[drawerLeading]" /></div>
      <div class="min-w-0 flex-1">
        <h2 appUiHeading="card" class="truncate" [id]="titleId">{{ headingText() }}</h2>
        @if (subtitle()) {
          <p class="m-0 mt-0.5 text-body-sm text-text-muted">{{ subtitle() }}</p>
        }
        <div class="mt-0.5 text-body-sm text-text-muted empty:hidden"><ng-content select="[drawerSubtitle]" /></div>
      </div>
      <div class="flex shrink-0 items-center gap-2 empty:hidden"><ng-content select="[drawerTrailing]" /></div>
      <app-ui-icon-button class="shrink-0" icon="close" ariaLabel="Close drawer" (click)="close()" />
    </header>

    <div
      class="flex min-h-0 flex-1 flex-col"
      [class.overflow-y-auto]="body() === 'padded'"
      [class.overscroll-contain]="body() === 'padded'"
      [class.p-6]="body() === 'padded'"
    >
      @switch (state()) {
        @case ('loading') {
          <p class="m-0 text-body-sm text-text-muted" [class.p-6]="body() === 'flush'" aria-live="polite">
            {{ loadingMessage() }}
          </p>
        }
        @case ('error') {
          <p class="m-0 text-body-sm text-text-muted" [class.p-6]="body() === 'flush'" role="alert">
            {{ errorMessage() }}
          </p>
        }
        @default {
          <ng-content />
        }
      }
    </div>
  `,
})
export class DrawerLayoutComponent {
  private readonly drawerService = inject(DrawerService);

  /** Visible title; it also names the drawer for assistive technology. */
  readonly title = input.required<string>();
  readonly subtitle = input<string | undefined>();
  readonly state = input<DrawerLayoutState>('ready');
  /**
   * `padded` scrolls the body with standard padding; `flush` hands layout and scrolling to
   * the content (no `app-drawer-footer` there, since it spans the padded body).
   */
  readonly body = input<DrawerLayoutBody>('padded');
  readonly loadingMessage = input('Loading…');
  readonly errorMessage = input("We couldn't load this. Close the drawer and try again.");

  protected readonly titleId = DRAWER_TITLE_ID;
  // The sheet and mobile panel are named only by this heading (aria-labelledby wins over
  // aria-label even when the heading is empty), so never render it blank.
  protected readonly headingText = computed(() => this.title().trim() || 'Details');

  close(): void {
    this.drawerService.closeDrawer();
  }
}
